import { useLocation, Navigate, useSearchParams } from 'react-router-dom'
import { searchStringToObject, objectToSearchString } from 'serialize-query-params'
import { boolean } from 'boolean'

import { useAuth } from 'hooks/useAuth'

export function PublicGuard({ children }: { children: JSX.Element }) {
	const location = useLocation()
	const [searchParams] = useSearchParams()
	const a = searchParams.get('a')
	const to = searchParams.get('to')
	const searchParamsString = objectToSearchString({ a, to })
	const { token, requireOtp, authInfo } = useAuth()
	const { from, inviteId } = location.state || {}

	if (requireOtp) {
		return (
			<Navigate
				to={`/auth/mfa${searchParamsString ? `?${searchParamsString}` : ''}`}
				state={{ from: location, inviteId }}
				replace
			/>
		)
	} else {
		if (token && authInfo) {
			const { isDeleted, isComplete, isPhoneNumberVerified, skipPasskeyRegistration } = authInfo

			if (boolean(isDeleted)) {
				return (
					<Navigate
						to={`/auth/recover${searchParamsString ? `?${searchParamsString}` : ''}`}
						state={{ from: location, inviteId }}
						replace
					/>
				)
			}

			if (!boolean(isComplete)) {
				return (
					<Navigate
						to={`/auth/sign-up-2${searchParamsString ? `?${searchParamsString}` : ''}`}
						state={{ from: location, inviteId }}
						replace
					/>
				)
			}

			if (from?.pathname) {
				const { urlToken, ...params } = searchStringToObject(from.search) || {}
				const searchString = objectToSearchString(params)
				if (urlToken) {
					return <Navigate to={`${from.pathname}${searchString ? `?${searchString}` : ''}`} replace />
				}
			}

			if (inviteId) {
				return <Navigate to={`/accept-invite/${inviteId}`} state={{ from: location }} replace />
			}

			if (['join-now'].includes(a!)) {
				return <Navigate to={`/legacy/pie-hub?a=${a}`} replace />
			}

			if (to) {
				return <Navigate to={to} replace />
			}

			if (['/auth/confirm-email'].includes(location.pathname)) {
				return children
			}

			// Check if passkey registration is required
			if (
				!boolean(skipPasskeyRegistration) &&
				import.meta.env.VITE_ENABLE_FEATURE_PASSKEY_REGISTRATION === 'true'
			) {
				return (
					<Navigate
						to={`/auth/register-passkey${searchParamsString ? `?${searchParamsString}` : ''}`}
						state={{ from: location, inviteId }}
						replace
					/>
				)
			}

			if (!boolean(isPhoneNumberVerified) && import.meta.env.VITE_ENABLE_FEATURE_VERIFY_PHONE_NUMBER === 'true') {
				if (['/auth/login'].includes(location.pathname)) {
					return (
						<Navigate
							to={`/auth/verify-phone-number${searchParamsString ? `?${searchParamsString}` : ''}`}
							state={{ from: location, inviteId }}
							replace
						/>
					)
				}
			}

			return <Navigate to="/" state={{ from: location }} replace />
		}
	}
	return children
}
